/** @jsxImportSource @emotion/react */
import { useCallback, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { useDispatch, useSelector } from 'react-redux';
import retryImage from '../assets/icons/retry.svg';
import { ApiError } from '../core/redux/api/apiError';
import { clearApiErrorAction } from '../core/redux/api/clearApiErrorAction';
import { IAppState } from '../core/redux/IAppState';
import { getResource } from '../language/resources/getResource';
import ErrorCountSummary from '../layout/ErrorCountSummary';
import { showSnackbarAction } from '../snackbar/snackbarAction';
import { FADE_IN_KEYFRAMES } from '../styles/animations';
import { BLUE_CREEK, COOL_GREY, DARK_BLUE_CREEK, WHITE } from '../styles/colors';
import { MediumText } from '../styles/styles';
import { makeRetryRequest, ProductHydrationErrors } from './hydrationErrors';
import { getRetryHydrationErrorsError, isRetryHydrationErrorsLoading, retryProductHydrationErrorsAction } from './retryProductHydrationErrorsAction';

const retryImageStyle = css`
  width: 15px;
  margin-right: 5px;
  pointer-events: initial;
  margin-bottom: -3px; /* align image with text */
`;

const retryButton = css`
  text-align: center;
  text-decoration: none;
  ${MediumText}
  animation: 1s ${FADE_IN_KEYFRAMES};
  cursor: pointer;

  background-color: ${BLUE_CREEK};
  color: ${WHITE};
  border: none;
  border-radius: 0.1rem;

  width: 115px;
  min-height: 35px;
  vertical-align: middle;
  position: relative;
  left: 25%;

  &:hover {
    background-color: ${DARK_BLUE_CREEK}; 
  }

  &:disabled {
    background-color: ${COOL_GREY};
    cursor: not-allowed !important;
  }
`;

const retryButtonContainer = css`
  display: grid; 
  grid-template-columns: auto 50px; 
  padding-top: 5px; 
  width: fit-content;
`;

const errorCountContainer = css`
  display: grid; 
  position: relative; 
  right: 21%; 
  top: -15px; 
  width: fit-content; 
  scale: 0.65;
  z-index: 1;
`;

const retryTextPosition = css`
  margin-right: 25%;
`;

type Props = {
  productName: string,
  fulfillmentSeasonDescription?: string,
  hydrationErrors?: ProductHydrationErrors,
};


/**
 * Hydration Error Retry Button for retrying product hydration errors
 *
 * PureComponent: true
 * Connected to Redux: true
 */
export const HydrationErrorRetryButton = (props: Props) => {
  const [enableRetry, setEnableRetry] = useState<boolean>(true);

  const {
    isRetrying,
    retryError,
    retryRequest,
    resortAbbrev,
    resources,
  } = useSelector((state: IAppState)  => {
      // One builder for the request the reducer keys on and the reads that look for it. Rebuilt on every
      // render, so never put it in a dependency array — an effect that dispatches would re-fire forever.
      const request = makeRetryRequest(state.selectedResort.resortAbbreviation, props.hydrationErrors);

      return {
        isRetrying: isRetryHydrationErrorsLoading(state, request),
        retryError: getRetryHydrationErrorsError(state, request),
        retryRequest: request,
        resortAbbrev: state.selectedResort.resortAbbreviation,
        resources: state.locale,
      };
    });

  const [retryButtonText, setRetryButtonText] = useState<string>(getResource(resources, 'PRODUCT_MAPPING_HYDRATION_ERRORS_RETRY'));

  const dispatch = useDispatch();

  const showSnackbar = useCallback((message: string) => dispatch(showSnackbarAction(message)) ,[dispatch]);
  const clearError = useCallback((error: ApiError) => dispatch(clearApiErrorAction(error.requestId, error.actionType)), [dispatch]);
  const showError = useCallback((errorMsg: string) => dispatch(showSnackbarAction(errorMsg, true)), [dispatch]);
  
  // Reset the button when the row it belongs to changes
  useEffect(() => {
    setRetryButtonText(getResource(resources, 'PRODUCT_MAPPING_HYDRATION_ERRORS_RETRY'));
    setEnableRetry(true);
  },[props.hydrationErrors, resortAbbrev, resources]);

  // Rows that share an entitlement type, season and age bin share this button's request id, so only the button
  // that started a retry reports how it went, or each of them announces every retry on that key. 'requested'
  // also covers the microtask before the middleware registers the request, when "not retrying" would
  // otherwise read as finished.
  const retry = useRef<'idle' | 'requested' | 'inFlight'>('idle');

  useEffect(() => {
    if (retry.current === 'idle') return;

    if (retryError) {
      retry.current = 'idle';
      clearError(retryError);
      showError(getResource(
        resources,
        'PRODUCT_MAPPING_HYDRATION_ERROR_RETRY_FAILURE',
        props.productName,
        props.fulfillmentSeasonDescription,
        props.hydrationErrors?.ageBin));
      setRetryButtonText(getResource(resources, 'PRODUCT_MAPPING_HYDRATION_ERRORS_RETRY'));
      setEnableRetry(true);
      return;
    }

    if (isRetrying) {
      retry.current = 'inFlight';
      return;
    }

    if (retry.current === 'inFlight') {
      retry.current = 'idle';
      showSnackbar(getResource(resources, 'PRODUCT_MAPPING_HYDRATION_ERRORS_RETRY_SUCCESS'));
    }
  },[clearError, isRetrying, props.fulfillmentSeasonDescription, props.hydrationErrors?.ageBin, props.productName, resources, retryError, showError, showSnackbar])


  const onRetryClick = () => {
    if (!retryRequest) return;
    retry.current = 'requested';
    setEnableRetry(false);
    setRetryButtonText(getResource(resources, 'PRODUCT_MAPPING_HYDRATION_ERRORS_RETRIED'));
    dispatch(retryProductHydrationErrorsAction(retryRequest));
  };

return (
  <span css={retryButtonContainer}>
    <button
      type="button"
      disabled={!enableRetry}
      onClick={onRetryClick}
      css={retryButton}>
      <img css={retryImageStyle} src={retryImage} alt="" title={getResource(resources, 'PRODUCT_MAPPING_HYDRATION_ERRORS_RETRY')} />
      <span css={retryTextPosition}>{retryButtonText}</span>
    </button>
    { 
      enableRetry && 
      <span css={errorCountContainer}>
        <ErrorCountSummary
          value={props.hydrationErrors?.errorCount ?? 0}
        ></ErrorCountSummary>
      </span>
    }
  </span>
)};
